import { type TrackFields } from '@/service/fragments/trackFieldsFrag'
import type { Maybe } from '@amazon/vinyl-util'
import { createShortUid, logError } from '@amazon/vinyl-util'
import type { PlaylistEntry } from '@/playback/PlaylistController'
import { MrnIdentifierType, parseMrn } from '@/util/mrn'
import { sanitizeId } from '@/util/entityId'
import { getLargestImage } from '@/util/imageUtil'
import { ctx } from '@/util/context'
import { getAuth } from '@/auth/authRef'

const { target } = ctx('trackGqlUtil')

export function trackToPlaylistEntry(track: TrackFields): PlaylistEntry | null {
    // Signed-in responses carry the entity MRN (catalog `…:asin:B0…`, or a
    // library `…:objectid:…` for owned/uploaded content); anonymous responses
    // omit `uri` (it's `@include(if: $isAuthenticated)`) but still expose the
    // bare asin via playParams.id — and sample streaming only needs the asin.
    // Prefer the MRN when present, and fall back to playParams.id so anon
    // tracks resolve too. playParams.id is sanitized: some feeds echo a full
    // MRN into it, which would otherwise produce a junk `sample://mrn:…` uri.
    const mrn = track.uri ? parseMrn(track.uri) : null
    const identifier = mrn?.identifier ?? sanitizeId(track.playParams?.id)
    if (!identifier) return null
    const artistName = track.contributingArtists?.concatenatedName ?? ''

    const image = getLargestImage(track.images, 'PRIMARY')

    if (!track.id) {
        logError(target, 'track missing id')
    }

    // Anonymous users can only play 30-second samples; signed-in users get
    // the full track. The player resolves these load types differently.
    const isSignedIn = getAuth().isSignedIn()
    // Owned/uploaded content (a purchased MP3 with no catalog asin) carries a
    // library `objectid` (COID) identifier and streams from DMLS by COID, not
    // the catalog asin path — vinyl-amzn's coid track factory handles it.
    const isOwnedCoid = mrn?.mediaIdentifierType === MrnIdentifierType.OBJECT_ID

    return {
        id: track.id ?? createShortUid(),
        entity: track,
        albumTitle: track.album?.shortTitle ?? track.album?.title ?? '',
        artistName,
        duration: track.duration ?? 0,
        image: image,
        title: track.title ?? '',
        trackLoadOptions: !isSignedIn
            ? {
                  // Anonymous: sample-only. The `sample://` scheme routes
                  // vinyl-amzn's SampleTrack to the media-central sample
                  // endpoint (DMLS with requireAuthorization=false) instead
                  // of the catalog endpoint that requires sign-in.
                  type: 'sample',
                  uri: `sample://${identifier}`,
              }
            : isOwnedCoid
              ? { type: 'coid', uri: `coid://${identifier}` }
              : { type: 'asin', uri: `asin://${identifier}` },
    }
}

export function getTrackArtistStr(track: Maybe<TrackFields>): string {
    return track?.contributingArtists?.concatenatedName ?? ''
}

export function getTrackAlbumTitle(track: Maybe<TrackFields>): string {
    // shortTitle is gated on $isAuthenticated — anonymous responses
    // omit it entirely. Fall back to the full title so the album
    // cell still reads on guest-mode pages (popular/songs, etc).
    return track?.album?.shortTitle ?? track?.album?.title ?? ''
}
